import React from "react";
import { useLoaderData, useParams } from "react-router";

import classNames from "classnames";
import { Col, Container, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import invariant from "tiny-invariant";

import ReplacementModal from "./components/MyScheduleModal";
import GamesTable from "./components/GamesTable";
import Ratings from "./components/Ratings";
import { MyScheduleModalProvider } from "./contexts/Modal";
import {
    MyScheduleProvider,
    useMyScheduleContext,
} from "./contexts/MySchedule";
import { ScheduleTypes } from "./types/myschedule";

import { store } from "../../..";
import "../../../assets/css/umphub/pages/myschedule.scss";
import Pagination from "../../../components/Pagination";
import SelectItemsPerPage from "../../../components/SelectItemsPerPage";
import UmpireInfoModal from "../../../components/UmpireInfoModal";
import { PaginationProvider } from "../../../contexts/Pagination";
import { UmpireInfoModalProvider } from "../../../contexts/UmpireInfoModal";
import { globalActions } from "../../../features/global/globalSlice";
import {
    getGamesByUmpire,
    getPastUmpireRatingAverages,
    getUmpireRatingAverages,
} from "../../../lib/api";
import { IGame, IUmpireRatingAverage } from "../../../types/api";
import { CoachInfoModalProvider } from "./contexts/CoachInfoModal";
import CoachInfoModal from "./components/CoachInfoModal";

export const loader = async (params: any) => {
    const { schedule } = params;
    const { settings, sites } = store.getState().global;
    const { user } = store.getState().auth;
    invariant(user);

    const promises = [];
    !settings.length &&
        promises.push(store.dispatch(globalActions.getSettings()));

    !sites.length && promises.push(store.dispatch(globalActions.getSites()));

    const [{ games }, { ratings: current }, { ratings: previous }] =
        await Promise.all([
            getGamesByUmpire(user.id, schedule),
            getUmpireRatingAverages(user.id, true),
            getPastUmpireRatingAverages(user.id),
            promises,
        ]);

    return {
        schedule,
        games,
        current,
        previous,
    };
};

const MySchedule = (): React.JSX.Element => {
    const { games, schedule, current, previous } = useLoaderData() as {
        schedule: ScheduleTypes;
        games: IGame[];
        current?: IUmpireRatingAverage;
        previous?: IUmpireRatingAverage;
    };

    return (
        <Container
            className={classNames("pt-3", "pb-5", "mySchedule")}
            data-testid="myScheduleContent"
        >
            <Row>
                <Col xl={{ offset: 1 }}>
                    <h3 className={classNames("mb-0")}>Ratings</h3>
                    <p>(minimum 5 rated games for average)</p>
                </Col>
            </Row>

            <Ratings current={current} previous={previous} />

            <Row className={classNames("mt-4")}>
                <Col>
                    <h3 className={classNames("mb-4")}>
                        {schedule === "current" ? "Upcoming" : "Past"} Schedule
                    </h3>
                </Col>
            </Row>
            <PaginationProvider>
                <SelectItemsPerPage />
                <MyScheduleProvider initialGames={games} schedule={schedule}>
                    <MyScheduleModalProvider>
                        <UmpireInfoModalProvider>
                            <CoachInfoModalProvider>
                                <GamesTable />
                                <UmpireInfoModal />
                                <CoachInfoModal />
                            </CoachInfoModalProvider>
                        </UmpireInfoModalProvider>
                        <ReplacementModal />
                    </MyScheduleModalProvider>
                    <Pagination useContext={useMyScheduleContext} />
                </MyScheduleProvider>
            </PaginationProvider>
        </Container>
    );
};

export default MySchedule;
